import { useEffect, useMemo, useState } from "react";
import { Clock3, ShieldCheck, TriangleAlert } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type {
  SafeOperationReadiness,
  SafeProbeEvidence,
  SafeReadinessReason,
} from "@/server/labsReadiness";

import { concreteKlingProviderLabel } from "./providerIdentity";

type ProviderReadinessWithOperations = {
  operation_details?: Record<string, SafeOperationReadiness>;
};

const PROBE_LABELS: Record<string, string> = {
  auth: "Authentication",
  schema: "Provider contract",
  credit: "Minimum-recipe credit",
  entitlement: "Entitlement",
};

const REMEDIATION: Record<string, string> = {
  CREDIT_PROBE_MISSING:
    "Wait for the scheduled check or inspect the readiness probe Lambda and Scheduler.",
  CREDIT_PROBE_EXPIRED:
    "Inspect the Scheduler, probe Lambda, lease, and evidence writer; readiness stays blocked until a fresh success lands.",
  CREDIT_PROBE_TRANSIENT_FAILURE:
    "Inspect provider reachability and probe alarms. A previous success is usable only until its recorded expiry.",
  CREDIT_INSUFFICIENT:
    "Top up the PiAPI-hosted account or verify an approved BYOA entitlement before enabling submissions.",
  CREDIT_AUTH_INVALID:
    "Repair the environment-local PiAPI secret reference and rerun the non-generating probe.",
  CREDIT_PROVIDER_CONTRACT_CHANGED:
    "Review the provider Account Info contract and update the strict parser before retrying.",
  CREDIT_EVIDENCE_INVALID:
    "Inspect the signed policy binding and evidence schema; malformed or future-dated evidence is rejected.",
  READINESS_OBSERVATIONS_INCOMPLETE:
    "Retry the health read and inspect DynamoDB throttling or unprocessed-key telemetry.",
  CONTROL_POLICY_DIGEST_MISMATCH:
    "Reconcile the active RDS release, signed snapshot, and service readback for this environment.",
  WORKER_REGISTRY_DIGEST_MISMATCH:
    "Deploy the worker revision pinned to the active registry digest.",
  DEPLOYMENT_REVISION_DRIFT:
    "Reconcile the active Batch job definition with the signed readiness policy.",
  OPERATION_POLICY_DISABLED:
    "Enable this exact provider/model/operation in a reviewed environment-local runtime-config release.",
  PROBE_POLICY_DISABLED:
    "Enable the provider probe policy in a reviewed environment-local runtime-config release.",
};

const dateValue = (value: string | undefined) => {
  const timestamp = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : null;
};

const absoluteUtc = (value: string | undefined) => {
  const timestamp = dateValue(value);
  if (timestamp === null) return "Not recorded";
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "medium",
    timeZone: "UTC",
  }).format(timestamp);
};

const relativeTime = (value: string | undefined, now: number) => {
  const timestamp = dateValue(value);
  if (timestamp === null) return "not recorded";
  const seconds = Math.round((timestamp - now) / 1000);
  const absolute = Math.abs(seconds);
  const [amount, unit]: [number, Intl.RelativeTimeFormatUnit] =
    absolute >= 86400
      ? [Math.round(seconds / 86400), "day"]
      : absolute >= 3600
        ? [Math.round(seconds / 3600), "hour"]
        : absolute >= 60
          ? [Math.round(seconds / 60), "minute"]
          : [seconds, "second"];
  return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(
    amount,
    unit,
  );
};

function selectedOperationReadiness(
  readiness: unknown,
  providerId: string,
  modelId: string,
) {
  const details = (readiness as ProviderReadinessWithOperations | null)
    ?.operation_details;
  return details?.[`${providerId}:${modelId}:image_to_video`] ?? null;
}

const probeState = (probe: SafeProbeEvidence | null, now: number) => {
  if (!probe) return { label: "Missing", variant: "destructive" as const };
  const expiry = dateValue(probe.expires_at);
  if (expiry !== null && expiry <= now) {
    return { label: "Expired", variant: "destructive" as const };
  }
  if (probe.status === "passed") {
    return { label: "Passed", variant: "default" as const };
  }
  return { label: probe.status ?? "Unknown", variant: "destructive" as const };
};

const Reason = ({ reason }: { reason: SafeReadinessReason }) => (
  <li className="space-y-1 rounded-md border border-border/70 bg-background/60 p-2">
    <code className="select-text text-xs font-semibold">
      {reason.code ?? "READINESS_BLOCKED"}
    </code>
    {reason.message ? <p className="text-xs">{reason.message}</p> : null}
    {reason.code && REMEDIATION[reason.code] ? (
      <p className="text-xs text-muted-foreground">
        Next: {REMEDIATION[reason.code]}
      </p>
    ) : null}
  </li>
);

export function ReadinessDiagnostics({
  providerId,
  modelId,
  readiness,
}: {
  providerId: string;
  modelId: string;
  readiness: unknown;
}) {
  const operation = selectedOperationReadiness(readiness, providerId, modelId);
  const [now, setNow] = useState(() => Date.now());
  const probes = useMemo(() => operation?.health?.probes ?? {}, [operation]);
  const expiries = useMemo(
    () =>
      Object.values(probes)
        .map((probe) => dateValue(probe?.expires_at))
        .filter((value): value is number => value !== null && value > now),
    [probes, now],
  );

  useEffect(() => {
    if (!expiries.length) return;
    const delay = Math.min(
      Math.min(...expiries) - Date.now() + 50,
      2_147_000_000,
    );
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.max(delay, 50),
    );
    return () => window.clearTimeout(timer);
  }, [expiries]);

  if (
    !operation ||
    (providerId !== "kling_piapi" && providerId !== "kling_direct")
  ) {
    return null;
  }

  const providerLabel = concreteKlingProviderLabel(providerId);
  const reasons = operation.reasons ?? [];
  const warnings = operation.warnings ?? [];

  return (
    <section
      aria-label={`${providerLabel} readiness diagnostics`}
      className="space-y-3 rounded-lg border bg-muted/30 p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {operation.ready ? (
            <ShieldCheck
              className="size-4 text-emerald-600"
              aria-hidden="true"
            />
          ) : (
            <TriangleAlert
              className="size-4 text-destructive"
              aria-hidden="true"
            />
          )}
          <h3 className="text-sm font-semibold">{providerLabel} readiness</h3>
        </div>
        <Badge variant={operation.ready ? "default" : "destructive"}>
          {(
            operation.state ?? (operation.ready ? "ready" : "blocked")
          ).replaceAll("_", " ")}
        </Badge>
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        {Object.entries(probes).map(([name, probe]) => {
          const state = probeState(probe, now);
          return (
            <article
              key={name}
              className="space-y-2 rounded-md border bg-background p-2"
            >
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-xs font-semibold">
                  {PROBE_LABELS[name] ?? name}
                </h4>
                <Badge variant={state.variant}>{state.label}</Badge>
              </div>
              <code className="block select-text break-all text-[11px]">
                {probe?.outcome_code ?? "NO_EVIDENCE"}
              </code>
              <p className="flex gap-1 text-[11px] text-muted-foreground">
                <Clock3 className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                <span>
                  Observed {relativeTime(probe?.observed_at, now)} ·{" "}
                  <time dateTime={probe?.observed_at}>
                    {absoluteUtc(probe?.observed_at)} UTC
                  </time>
                </span>
              </p>
              <p className="text-[11px] text-muted-foreground">
                Expires {relativeTime(probe?.expires_at, now)} ·{" "}
                <time dateTime={probe?.expires_at}>
                  {absoluteUtc(probe?.expires_at)} UTC
                </time>
              </p>
              {name === "credit" && probe?.minimum_recipe_estimate_usd ? (
                <p className="text-[11px] text-muted-foreground">
                  Proves at least the catalogued least-cost recipe ($
                  {probe.minimum_recipe_estimate_usd}). Exact submission
                  affordability can still change before a run.
                </p>
              ) : null}
            </article>
          );
        })}
      </div>

      {reasons.length || warnings.length ? (
        <ul className="space-y-2" aria-label="Readiness reasons">
          {[...reasons, ...warnings].map((reason, index) => (
            <Reason
              key={`${reason.code ?? "reason"}-${index}`}
              reason={reason}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}
